import { EmptyState, Pagination, SelectField, Table, type TableColumn } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { IMPORT_COUNT_KEYS, type ImportExecutionRowView, type Option, type Translate } from './import-monitoring-types'

/**
 * Props for {@link ImportExecutionListView}.
 *
 * @property rows - The current page, newest first, already formatted for display.
 * @property statusFilter - The selected `ImportExecutionStatus`, or `''` for all.
 * @property logicalFileFilter - The selected `LogicalFileType`, or `''` for all.
 * @property error - True when the last request failed: renders the retry state.
 */
export interface ImportExecutionListViewProps {
	t: Translate
	rows: ImportExecutionRowView[]
	statusFilter: string
	statusOptions: Option[]
	logicalFileFilter: string
	logicalFileOptions: Option[]
	page: number
	pageCount: number
	error: boolean
	onStatusChange: (status: string) => void
	onLogicalFileChange: (logicalFile: string) => void
	onPageChange: (page: number) => void
	onOpen: (id: string) => void
	onRetry: () => void
}

const columns = (t: Translate, onOpen: (id: string) => void): TableColumn<ImportExecutionRowView>[] => [
	{
		key: 'fileName',
		header: t('list.columns.file'),
		cell: (row) => (
			<Button variant="muted" size="sm" onPress={() => onOpen(row.id)}>
				{row.fileName}
			</Button>
		),
	},
	{ key: 'logicalFileLabel', header: t('list.columns.logicalFile') },
	{ key: 'transportLabel', header: t('list.columns.transport') },
	{ key: 'definition', header: t('list.columns.definition') },
	{ key: 'receivedAt', header: t('list.columns.received') },
	{ key: 'statusLabel', header: t('list.columns.status') },
	...IMPORT_COUNT_KEYS.map((count) => ({
		key: `counts.${count}`,
		header: t(`counts.${count}`),
		cell: (row: ImportExecutionRowView) => row.counts[count],
	})),
]

/** The tenant's import executions: status and logical-file filters, table and paging (organism). */
export function ImportExecutionListView(props: ImportExecutionListViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-4">
			<h2 className="text-ds-lg font-semibold text-ds-text">{t('list.title')}</h2>
			<div className="flex flex-wrap gap-4">
				<SelectField
					label={t('list.filters.status')}
					value={props.statusFilter}
					onValueChange={props.onStatusChange}
					options={props.statusOptions.map((o) => ({ id: o.id, text: o.label }))}
				/>
				<SelectField
					label={t('list.filters.logicalFile')}
					value={props.logicalFileFilter}
					onValueChange={props.onLogicalFileChange}
					options={props.logicalFileOptions.map((o) => ({ id: o.id, text: o.label }))}
				/>
			</div>
			{props.error ? (
				<EmptyState title={t('list.error')} action={{ label: t('common.retry'), onPress: props.onRetry }} />
			) : (
				<Table
					aria-label={t('list.title')}
					columns={columns(t, props.onOpen)}
					rows={props.rows}
					emptyState={<EmptyState title={t('list.empty')} />}
				/>
			)}
			<Pagination page={props.page} pageCount={props.pageCount} onPageChange={props.onPageChange} />
		</section>
	)
}
